/**
 * ACTIVITY-CORE (L3, POS variant) — "Activity" page. Presentation only.
 *
 * PLUGIN-KERNEL: the data half (local mirror query, deep-history paging, the
 * shift narrowing and the teardown on account switch) lives in the activity
 * plugin (`@comandero/shared/solid`), mounted by
 * `apps/web/src/kernel/composition.ts`. What stays here is what is genuinely
 * this product's: the shift selector and the antifraud summary for the cash-up.
 */
import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, Show } from "solid-js";
import { History, Percent, RotateCcw, XCircle } from "@fleet/icons";
import { getSessionsQuery, queryKeys, summarizeShiftActivity } from "@comandero/shared";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { ACTIVITY_FEED } from "@fleet/plugins/activity";
import type { RegisterSession } from "@comandero/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { useKernelService } from "@fleet/kernel";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";
import { ALL_SESSIONS, setShiftSession, shiftSessionId } from "@/features/activity/shift-filter";
import { ActivityList } from "./activity-list";

export function ActivityFeedPage(props: { initialSessionId?: string }) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const ctx = useActiveContext();
  const feed = useKernelService(ACTIVITY_FEED);

  // The shift filter is read by the plugin, so it lives outside the page. It is
  // seeded from the URL (?session=…, the cash-up link) and is plain state
  // afterwards — no URL→state effect that could revert the selection (CC-16).
  createEffect(() => {
    if (props.initialSessionId) {
      setShiftSession(props.initialSessionId);
    }
  });

  // Cash-register sessions for the active branch (for the shift selector).
  const sessionsQuery = createPowerSyncQuery<RegisterSession>({
    queryKey: () => queryKeys.comandero.sessions(ctx.branch()?.id ?? "none"),
    db,
    queryFn: () => {
      const d = drizzle();
      const org = ctx.org();
      const branch = ctx.branch();
      return d && org && branch ? getSessionsQuery(d, org.id, branch.id) : null;
    },
    enabled: () => Boolean(drizzle() && ctx.org() && ctx.branch()),
  });

  const sessionOptions = createMemo(() => {
    const opts = [{ value: ALL_SESSIONS, label: t(msg`All activity`) }];
    for (const s of sessionsQuery.data ?? []) {
      const opened = s.opened_at ? new Date(s.opened_at).toLocaleString(locale()) : s.id;
      const status = s.status === "open" ? t(msg`open`) : t(msg`closed`);
      opts.push({ value: s.id, label: `${opened} — ${status}` });
    }
    return opts;
  });

  const rows = () => feed()?.rows() ?? [];

  // Antifraud summary for the selected shift (for cash-up).
  const shiftSummary = createMemo(() => (shiftSessionId() ? summarizeShiftActivity(rows()) : null));

  return (
    <div class="space-y-6" data-testid="activity-page">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <History class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-2xl font-bold tracking-tight">{t(msg`Activity`)}</h1>
            <p class="text-sm text-muted-foreground">
              {t(msg`Who did what in your restaurant — every void, discount and reopen.`)}
            </p>
          </div>
        </div>
        <div class="min-w-52">
          <Select
            options={sessionOptions()}
            value={shiftSessionId() ?? ALL_SESSIONS}
            onChange={(value) => setShiftSession(value)}
            aria-label={t(msg`Filter by register session`)}
            data-testid="activity-session-filter"
            fluid
          />
        </div>
      </div>

      <Show when={shiftSummary()}>
        {(summary) => (
          <div
            class="grid grid-cols-3 gap-3 rounded-xl border border-border/30 bg-card/60 p-4"
            data-testid="shift-summary"
          >
            <div class="flex items-center gap-2.5">
              <XCircle class="h-4 w-4 text-destructive" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-voids">
                  {summary().voids}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Voids`)}</p>
              </div>
            </div>
            <div class="flex items-center gap-2.5">
              <Percent class="h-4 w-4 text-warning" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-discounts">
                  {summary().discounts}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Discounts`)}</p>
              </div>
            </div>
            <div class="flex items-center gap-2.5">
              <RotateCcw class="h-4 w-4 text-primary" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-reopens">
                  {summary().reopens}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Reopens`)}</p>
              </div>
            </div>
          </div>
        )}
      </Show>

      <ActivityList
        rows={rows}
        isLoading={() => feed()?.isLoading() ?? true}
        grouped
        currentUserId={userId}
        canRevert
      />

      <Show when={feed()?.hasMore()}>
        <div class="flex justify-center pb-6">
          <Button
            variant="outline"
            size="sm"
            disabled={feed()?.isLoadingOlder()}
            onClick={() => void feed()?.loadOlder()}
            data-testid="activity-load-older"
          >
            {feed()?.isLoadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
          </Button>
        </div>
      </Show>
    </div>
  );
}
